/**
 * ============================================
 * NOTIFICATION UTILITIES
 * ============================================
 * 
 * PWA notification and alarm sound utilities.
 * User-configurable settings for alarm behavior.
 * Supports custom alarm sounds.
 */

import type { Alert } from '@/types';

// ============================================
// SETTINGS (persisted to localStorage)
// ============================================

const SETTINGS_KEY = 'lokko_alert_settings';
const CUSTOM_SOUND_KEY = 'lokko_custom_alarm_sound';

export type AlarmMode = 'every_refresh' | 'cooldown';

export interface AlertSettings {
  /** Master switch for all alerts */
  enabled: boolean;
  /** Volume level 0.0 to 1.0 */
  volume: number;
  /** Alarm mode: every refresh or with cooldown */
  alarmMode: AlarmMode;
  /** Cooldown in minutes (only used if alarmMode is 'cooldown') */
  cooldownMinutes: number;
  /** Per-environment enable/disable */
  enabledEnvironments: Record<string, boolean>;
  /** Use custom sound instead of default */
  useCustomSound: boolean;
  /** Custom sound filename for display */
  customSoundName: string | null;
}

const DEFAULT_SETTINGS: AlertSettings = {
  enabled: true,
  volume: 1.0,
  alarmMode: 'every_refresh',
  cooldownMinutes: 15,
  enabledEnvironments: {
    dev: true,
    stage: true,
    live: true,
  },
  useCustomSound: false,
  customSoundName: null,
};

export function getAlertSettings(): AlertSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
        // Deep-merge the nested env map so a partial/older saved object can't
        // drop defaults for environments it didn't include.
        enabledEnvironments: { ...DEFAULT_SETTINGS.enabledEnvironments, ...(parsed?.enabledEnvironments || {}) },
      };
    }
  } catch (e) {
    console.error('Failed to load alert settings:', e);
  }
  return DEFAULT_SETTINGS;
}

export function saveAlertSettings(settings: Partial<AlertSettings>): AlertSettings {
  const current = getAlertSettings();
  const updated = { ...current, ...settings };
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updated));
  } catch (e) {
    console.error('Failed to save alert settings:', e);
  }
  return updated;
}

export function setAlertVolume(volume: number): void {
  const newVolume = Math.max(0, Math.min(1, volume));
  saveAlertSettings({ volume: newVolume });
  if (alarmAudio) {
    alarmAudio.volume = newVolume;
  }
}

export function setAlertsEnabled(enabled: boolean): void {
  saveAlertSettings({ enabled });
  if (!enabled && isAlarmPlaying()) {
    stopAlarm();
  }
}

export function setAlarmMode(mode: AlarmMode): void {
  saveAlertSettings({ alarmMode: mode });
}

export function setCooldownMinutes(minutes: number): void {
  saveAlertSettings({ cooldownMinutes: Math.max(1, minutes) });
}

export function setEnvironmentEnabled(envId: string, enabled: boolean): void {
  const current = getAlertSettings();
  const updatedEnvs = { ...current.enabledEnvironments, [envId]: enabled };
  saveAlertSettings({ enabledEnvironments: updatedEnvs });
}

export function isEnvironmentEnabled(envId: string): boolean {
  const settings = getAlertSettings();
  return settings.enabledEnvironments[envId] ?? true;
}

export function getUserCooldownMs(): number {
  const settings = getAlertSettings();
  if (settings.alarmMode === 'every_refresh') {
    return 0;
  }
  return settings.cooldownMinutes * 60 * 1000;
}

// ============================================
// CUSTOM ALARM SOUND
// ============================================

export async function saveCustomSound(file: File): Promise<boolean> {
  try {
    // Validate file type
    if (!file.type.startsWith('audio/')) {
      console.error('Invalid file type:', file.type);
      return false;
    }
    
    // Limit file size to 5MB
    if (file.size > 5 * 1024 * 1024) {
      console.error('File too large:', file.size);
      return false;
    }

    // Convert to base64
    const base64 = await fileToBase64(file);
    localStorage.setItem(CUSTOM_SOUND_KEY, base64);
    saveAlertSettings({ useCustomSound: true, customSoundName: file.name });
    
    // Clear cached sound URL
    customSoundUrl = null;
    
    console.log('✅ Custom alarm sound saved:', file.name);
    return true;
  } catch (e) {
    console.error('Failed to save custom sound:', e);
    return false;
  }
}

export function removeCustomSound(): void {
  localStorage.removeItem(CUSTOM_SOUND_KEY);
  saveAlertSettings({ useCustomSound: false, customSoundName: null });
  customSoundUrl = null;
  console.log('🗑️ Custom alarm sound removed');
}

export function hasCustomSound(): boolean {
  return localStorage.getItem(CUSTOM_SOUND_KEY) !== null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

let customSoundUrl: string | null = null;

function getCustomSoundUrl(): string | null {
  if (customSoundUrl) return customSoundUrl;
  
  const base64 = localStorage.getItem(CUSTOM_SOUND_KEY);
  if (base64) {
    customSoundUrl = base64;
    return customSoundUrl;
  }
  return null;
}

// ============================================
// NOTIFICATION PERMISSION
// ============================================

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('⚠️ Notifications not supported');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    console.log(`🔔 Notification permission: ${permission}`);
    return permission;
  }

  return Notification.permission;
}

export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

// ============================================
// SEND NOTIFICATION
// ============================================

// Rate limiting for notifications - prevent spam
const notificationCooldowns = new Map<string, number>();
const NOTIFICATION_COOLDOWN_MS = 5000; // 5 seconds minimum between same notifications

export function sendNotification(alert: Alert): void {
  const settings = getAlertSettings();
  if (!settings.enabled) return;
  if (!isEnvironmentEnabled(alert.environmentId)) return;

  if (Notification.permission !== 'granted') {
    console.warn('⚠️ Notification permission not granted');
    return;
  }

  // Rate limit by alert type + environment (not by ID, since IDs are always unique)
  const cooldownKey = `${alert.environmentId}:${alert.type}`;
  const lastNotification = notificationCooldowns.get(cooldownKey);
  const now = Date.now();
  
  if (lastNotification && now - lastNotification < NOTIFICATION_COOLDOWN_MS) {
    console.log(`🔕 Notification rate limited: ${cooldownKey}`);
    return;
  }
  
  notificationCooldowns.set(cooldownKey, now);

  try {
    const notification = new Notification(alert.title, {
      body: alert.message,
      icon: '/icons/lokko-512.png',
      badge: '/icons/lokko.png',
      tag: cooldownKey, // Use type+env as tag to replace duplicates
      requireInteraction: alert.severity === 'critical',
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    console.log(`🔔 Notification sent: ${alert.title}`);
  } catch (error) {
    console.error('❌ Failed to send notification:', error);
  }
}

// ============================================
// ALARM SOUND
// ============================================

let alarmAudio: HTMLAudioElement | null = null;
let alarmPlaying = false;

// Default alarm sound path (in public folder)
const DEFAULT_ALARM_PATH = '/alarm.mp3';

function getAlarmSoundUrl(): string {
  const settings = getAlertSettings();
  if (settings.useCustomSound) {
    const customUrl = getCustomSoundUrl();
    if (customUrl) return customUrl;
  }
  return DEFAULT_ALARM_PATH;
}

/**
 * Audible alarms are temporarily DISABLED across all Overseer clients while the
 * Amazon Connect voice-alert escalation (see Confluence/08-Voice-Alert-Escalation-Plan)
 * is being built. Push/local NOTIFICATIONS remain fully active. Flip this back to
 * false to restore audible alarms.
 */
const ALARMS_DISABLED = true;

/**
 * Play alarm sound
 */
export function playAlarm(): void {
  if (ALARMS_DISABLED) {
    return;
  }
  const settings = getAlertSettings();
  console.log('🔊 playAlarm called. Settings:', settings);
  
  if (!settings.enabled) {
    console.log('🔕 Alarm disabled in settings, not playing');
    return;
  }
  
  // CRITICAL: Check flag FIRST to prevent multiple audio instances
  if (alarmPlaying) {
    console.log('🔔 Alarm already playing, skipping');
    return;
  }
  
  // CRITICAL: Set flag IMMEDIATELY before any async operations
  // This prevents race conditions where multiple calls create multiple Audio objects
  alarmPlaying = true;

  try {
    console.log('🎵 Creating alarm audio...');
    const soundUrl = getAlarmSoundUrl();
    console.log('🎵 Using sound:', settings.useCustomSound ? 'custom' : 'default');
    
    alarmAudio = new Audio(soundUrl);
    alarmAudio.loop = true;
    alarmAudio.volume = settings.volume;
    
    const playPromise = alarmAudio.play();
    
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('🚨 Alarm started playing successfully');
        })
        .catch((error) => {
          console.error('❌ Alarm play failed, trying AudioContext fallback:', error);
          // Reset flag since HTML Audio failed, fallback will set it
          alarmPlaying = false;
          playAlarmWithAudioContext();
        });
    }
  } catch (error) {
    console.error('❌ Failed to create alarm:', error);
    alarmPlaying = false;
    playAlarmWithAudioContext();
  }
}

// Fallback using AudioContext (only for default sound)
let audioCtx: AudioContext | null = null;
let oscillator: OscillatorNode | null = null;
let gainNode: GainNode | null = null;
let pulseInterval: number | null = null;

function playAlarmWithAudioContext(): void {
  console.log('🎵 Attempting AudioContext fallback...');
  
  // CRITICAL: Check if already playing to prevent multiple oscillators
  if (alarmPlaying) {
    console.log('🔔 AudioContext: Alarm already playing, skipping');
    return;
  }
  
  // Set flag immediately
  alarmPlaying = true;
  
  try {
    const settings = getAlertSettings();
    
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      console.log('🎵 Created new AudioContext');
    }
    
    if (audioCtx.state === 'suspended') {
      console.log('🎵 Resuming suspended AudioContext...');
      audioCtx.resume();
    }
    
    gainNode = audioCtx.createGain();
    gainNode.gain.value = settings.volume;
    gainNode.connect(audioCtx.destination);
    
    oscillator = audioCtx.createOscillator();
    oscillator.type = 'square';
    oscillator.frequency.value = 880;
    oscillator.connect(gainNode);
    oscillator.start();
    
    let soundOn = true;
    pulseInterval = window.setInterval(() => {
      if (gainNode) {
        gainNode.gain.value = soundOn ? settings.volume : 0;
        soundOn = !soundOn;
      }
    }, 400);
    
    console.log('🚨 Alarm started via AudioContext fallback');
  } catch (error) {
    console.error('❌ AudioContext fallback failed:', error);
    alarmPlaying = false; // Reset flag on failure
  }
}

/**
 * Stop the alarm
 */
export function stopAlarm(): void {
  console.log('🔕 stopAlarm called. Current state: alarmPlaying=', alarmPlaying);
  
  if (alarmAudio) {
    alarmAudio.pause();
    alarmAudio.currentTime = 0;
    alarmAudio = null;
    console.log('🔕 HTML Audio stopped');
  }
  
  if (pulseInterval) {
    clearInterval(pulseInterval);
    pulseInterval = null;
  }
  
  if (oscillator) {
    try {
      oscillator.stop();
      oscillator.disconnect();
    } catch (e) {
      // Ignore
    }
    oscillator = null;
    console.log('🔕 AudioContext oscillator stopped');
  }
  
  if (gainNode) {
    gainNode.disconnect();
    gainNode = null;
  }
  
  alarmPlaying = false;
  console.log('🔕 Alarm stopped, alarmPlaying set to false');
}

/**
 * Check if alarm is currently playing
 */
export function isAlarmPlaying(): boolean {
  return alarmPlaying;
}

// ============================================
// TRIGGER ALERT (NOTIFICATION + ALARM)
// ============================================

export function triggerAlert(alert: Alert): void {
  const settings = getAlertSettings();
  if (!settings.enabled) return;
  if (!isEnvironmentEnabled(alert.environmentId)) return;

  // Skip BOTH notification and alarm for already-acknowledged alerts.
  // Without this, reloading the page caused acknowledged alerts to be
  // re-detected as 'new' (fresh AlertContext, fresh knownAlertIdsRef)
  // and re-fire lock-screen banners for alarms the user had already
  // silenced. With it, ack'd alerts restore quietly into the bell.
  if (alert.acknowledged) {
    return;
  }

  sendNotification(alert);

  // playAlarm only for criticals (the !acknowledged check above already
  // gated us, but keep severity check for clarity).
  if (alert.severity === 'critical') {
    playAlarm();
  }
}